<template>
	<div
		class="cog-checkable cog-input"
		:class="[{'cog-checkable--allow-other': allowOther}, hasColumns ? columnClasses : 'cog-checkable--inline', readonly ? 'is-read-only' : '']"
	>
		<div v-if="readonly" :class="{'cog-image-item': selectedImage.value}"><template v-if="selectedImage.value"><c-generated-avatar v-if="isSvg(selectedImage.value)" :svg="selectedImage.value" /><div v-else-if="selectedImage.type === 'choiceImage'" :style="{ backgroundImage: `url(${selectedImage.value})`}" :class="['cog-choice-image', choiceImageSizeClass]" /><img v-else :src="selectedImage.value" height="50" width="50" class="cog-lookup-image cog-avatar"	/></template>{{ displayValue | breakWords | spaceIfEmpty }}</div>
		<el-radio-group
			v-else
			v-model="internalValue"
			:role="null"
			:label="null"
			@change="handleChange"
		>
			<el-radio
				v-for="(item, i) in options"
				:key="item.value"
				:label="item.value"
				:value="item.value"
				class="cog-checkable__item"
				:class="hasColumns ? 'cog-inner-col__item' : null"
				:style="`order: ${optionOrder[i]}`"
				@keydown.space.native="handleRadioSpace"
				@click.native="$emit('focus-lost')"
			>
				<div :class="{'cog-image-item': item.image || item.svg || item.choiceImage}">
					<c-generated-avatar v-if="item.svg" :svg="item.svg" />
					<div v-else-if="item.choiceImage" :style="{ backgroundImage: `url(${item.choiceImage})`}" :class="['cog-choice-image', choiceImageSizeClass]" />
					<img v-else-if="item.image" :src="item.image" height="50" width="50" class="cog-lookup-image cog-avatar" />
					{{ item.displayValue | breakWords }}
				</div>
			</el-radio>
			<el-radio
				v-if="allowOther"
				ref="otherRadio"
				:style="`order: ${optionOrder[options.length]}`"
				:class="hasColumns ? 'cog-inner-col__item' : null"
				class="cog-checkable__item cog-checkable__allow-other"
				:label="otherValue"
				@change="handleOtherRadioChange"
				@click.native="handleOtherRadioClick"
			>
				<el-input
					ref="otherInput"
					v-model="otherValue"
					:tabindex="otherIsSelected ? '0' : '-1'"
					:placeholder="$resource('field-choice-other-fill-in')"
					@focus="handleOtherFocus"
					@blur="handleOtherBlur"
					@input="handleOtherInput"
				/>
			</el-radio>
		</el-radio-group>
	</div>
</template>

<script>
	import '../style/_fancy-checkables.scss';

	import Vue from 'vue';
	import { RadioGroup, Radio, Input } from '@cognitoforms/element-ui';
	import columnizedOptions from 'src/mixins/columnized-options';
	import resetComponentData from 'src/mixins/resetComponentData';
	import CGeneratedAvatar from './GeneratedAvatar.vue';
	import { getChoiceImageSizeClass } from './use-choice-image-size';

	Vue.use(RadioGroup);
	Vue.use(Radio);
	Vue.use(Input);

	export default Vue.extend({
		name: 'CRadioGroup',
		components: { CGeneratedAvatar },
		filters: {
			spaceIfEmpty(value) {
				// https://stackoverflow.com/a/19810263/2836695
				return value || '\xa0';
			}
		},
		mixins: [columnizedOptions, resetComponentData],
		inject: ['$resource'],
		inheritAttrs: false,
		model: {
			prop: 'value',
			event: 'change'
		},
		props: {
			value: { type: String, default: null },
			readonly: Boolean,
			allowOther: Boolean,
			choiceImageSize: { type: Number, default: 0 },
			hideChoiceLabels: { type: Boolean, default: false }
		},
		data() {
			return {
				internalValue: this.value,
				// The other value being undefined keeps it from being selected
				// (the radio is selected if its label is the same as the group's value, `internalValue`, which is blank string)
				otherValue: this.isOtherValue(this.value) ? this.value : undefined
			};
		},
		computed: {
			selectedValue() {
				return this.options.find(o => o.value === this.value);
			},
			displayValue() {
				if (this.value) {
					if (this.selectedValue)
						return this.selectedValue.displayValue;
					else if (this.allowOther)
						return this.value;
				}
				return '';
			},
			otherIsSelected() {
				return this.internalValue === this.otherValue;
			},
			selectedImage() {
				if (this.value) {
					if (this.selectedValue?.choiceImage) {
						return { type: 'choiceImage', value: this.selectedValue?.choiceImage };
					}
					else if (this.selectedValue?.image) {
						return { type: 'image', value: this.selectedValue?.img };
					}
					else if (this.selectedValue?.svg) {
						return { type: 'svg', value: this.selectedValue?.svg };
					}

					return { type: null, value: '' };
				}
				return '';
			},
			choiceImageSizeClass() {
				return getChoiceImageSizeClass(this.choiceImageSize);
			}
		},
		watch: {
			value: {
				immediate: true,
				handler(value) {
					this.internalValue = value;
					if (this.isOtherValue(value))
						this.otherValue = value;
				}
			},
			internalValue: {
				immediate: false,
				async handler(value) {
					await this.$nextTick();
					this.setTabIndex();
				}
			}
		},
		created () {
			this.changeHandlerName = ['handleChange', 'handleOtherBlur'];
		},
		methods: {
			isOtherValue(value) {
				return !!value && this.allowOther && !this.options.map(o => o.value).includes(value);
			},
			handleOtherFocus() {
				this.updating = true;

				// If the other option is undefined when focussed, then set it to blank string to cause it to be selected
				if (!this.otherValue)
					this.otherValue = '';

				// Treat focus on the "Other" textbox as its first input event (which will seed the internal value with blank string)
				this.handleOtherInput();

				if (this.internalValue !== this.value) {
					// Emit change when "Other" is focused if there is a value, since the blank other value is now selected
					this.$emit('change', this.internalValue);
				}
			},
			handleOtherInput() {
				// Change the internal value to match the other value when it is selected or changed
				this.internalValue = this.otherValue;

				this.$emit('input', this.internalValue);
			},
			handleOtherBlur(ev) {
				// If the other value is blank when blurred, then set the value to `undefined` to cause it to be de-selected
				if (!this.otherValue)
					this.otherValue = undefined;

				// Raise the change event if the value has actually changed (i.e. the user didn't simply focus into the input then leave)
				if (this.internalValue !== this.value) {
					this.$emit('change', this.internalValue);
				}
				this.setTabIndex();
			},
			async handleOtherRadioChange(e) {
				await this.$nextTick();
				this.$refs.otherInput.focus();
			},
			async handleOtherRadioClick(e) {
				await this.$nextTick();
				this.$refs.otherInput.focus();
			},
			handleChange(value) {
				this.internalValue = value;
				this.$emit('change', this.internalValue);
				this.setTabIndex();
			},
			handleRadioSpace() {
				this.$emit('change', this.internalValue);
			},
			setTabIndex() {
				const firstRadio = this.$el.querySelector('.el-radio');

				if (!firstRadio)
					return;

				// If the first radio is checked, tabbing order will be correct.
				if (firstRadio.getAttribute('aria-checked') === 'true') {
					firstRadio.setAttribute('tabindex', '0');
					return;
				}

				setTimeout(()=> {
					const checked = this.$el.querySelector('[aria-checked]');

					// If nothing is checked, put tabindex=0 on the first radio. Element does not account for deselecting radio groups.
					// Otherwise, if a radio other than the first is checked, take the first radio out of tabbing order. This fixes a bug in Element.
					if (!checked)
						firstRadio.setAttribute('tabindex', '0');
					else
						firstRadio.setAttribute('tabindex', '-1');
				});
			},
			isSvg(image) {
				return image && image.startsWith('<svg viewBox=');
			}
		}
	});
</script>